import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { registrationsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { UserPlus, Check, X, Mail, Clock, MessageSquare, Briefcase } from 'lucide-react';

const ROLE_LABELS = {
  collaborator: { label: 'Azienda', color: 'bg-blue-100 text-blue-800' },
  gerente: { label: 'Gerente', color: 'bg-green-100 text-green-800' },
  medico_competente: { label: 'Medico Competente', color: 'bg-purple-100 text-purple-800' },
  docente: { label: 'Docente', color: 'bg-orange-100 text-orange-800' },
};

export default function RegistrationsPage() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRegistrations();
  }, []);

  const loadRegistrations = async () => {
    try {
      const response = await registrationsApi.getPending();
      setRegistrations(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle richieste');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id, name) => {
    try {
      await registrationsApi.approve(id);
      toast.success(`Utente ${name} approvato con successo`);
      loadRegistrations();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  const handleReject = async (id) => {
    try {
      await registrationsApi.reject(id);
      toast.success('Richiesta rifiutata');
      loadRegistrations();
    } catch (error) {
      toast.error('Errore nel rifiuto');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl">Richieste di Accesso</h1>
          <p className="text-muted-foreground mt-1">Gestisci le richieste di registrazione degli utenti</p>
        </div>
        <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg">
          <UserPlus className="w-5 h-5 text-primary" />
          <span className="font-semibold">{registrations.length} in attesa</span>
        </div>
      </div>

      {registrations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <UserPlus className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">Nessuna richiesta in attesa</h3>
            <p className="text-muted-foreground">Tutte le richieste di accesso sono state gestite</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {registrations.map((reg, index) => (
            <motion.div
              key={reg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-semibold text-primary">
                            {reg.full_name?.[0]?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{reg.full_name}</h3>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            {reg.email}
                          </div>
                        </div>
                      </div>
                      
                      {reg.requested_role && (
                        <div className="mt-3 flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Ruolo richiesto:</span>
                          <Badge className={ROLE_LABELS[reg.requested_role]?.color || 'bg-gray-100 text-gray-800'}>
                            {ROLE_LABELS[reg.requested_role]?.label || reg.requested_role}
                          </Badge>
                        </div>
                      )}
                      
                      {reg.message && (
                        <div className="mt-4 p-3 bg-muted rounded-lg">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                            <MessageSquare className="w-4 h-4" />
                            Messaggio
                          </div>
                          <p className="text-sm">{reg.message}</p>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-1 mt-3 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        Richiesta: {new Date(reg.created_at).toLocaleDateString('it-IT', { 
                          day: '2-digit', 
                          month: 'long', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => handleApprove(reg.id, reg.full_name)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-${reg.id}`}
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approva
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleReject(reg.id)}
                        data-testid={`reject-${reg.id}`}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
